import {
  MetaInterface,
  OfferInterface,
  TimeslotInterface,
  TimeslotStatusEnum,
} from '@sport-activities/sdk';
import { formatDuration, intervalToDuration } from 'date-fns';

import { isEmpty } from '~/core/helpers/object.helper';

export class Timeslot implements TimeslotInterface {
  public activityIdentifier: string;
  public bookableUntilTheEnd: boolean;
  public childId: string;
  public endDate: Date;
  public endDateTimezone: number;
  public groups?: string[];
  public identifier: string;
  public liveUrl: string | null;
  public maximumAttendeeCapacity: number;
  public meta: MetaInterface | null;
  public minimumAttendeeCapacity: number;
  public noteForAttendees: string | null;
  public occurenceNumber: string;
  public offers: OfferInterface[];
  public remainingAttendeeCapacity: number;
  public startDate: Date;
  public startDateTimezone: number;
  public status: TimeslotStatusEnum;
  public tenantName: string;

  constructor(init: Partial<TimeslotInterface>) {
    Object.assign(this, init);
  }

  public get isOutdated(): boolean {
    return new Date(this.startDate) < new Date();
  }

  public get maxPriceOffer(): OfferInterface {
    return [...this.offers]?.sort((a, b) => b.price - a.price)?.[0];
  }

  public get minPriceOffer(): OfferInterface {
    return [...this.offers]?.sort((a, b) => a.price - b.price)?.[0];
  }

  public get totalBooked(): number {
    return this.maximumAttendeeCapacity - this.remainingAttendeeCapacity;
  }

  private isOfferExternal(offer: OfferInterface): boolean {
    if (offer.identifier !== 'external' || isEmpty(offer.meta)) {
      return false;
    }
    return String(offer.meta.booking).startsWith('https://');
  }

  public formattedDuration(
    options: {
      delimiter?: string;
      format?: string[];
      locale?: Locale;
      zero?: boolean;
    },
    isCourse = false,
    isEvent = false
  ): string {
    let duration = intervalToDuration({
      start: new Date(this.startDate),
      end: new Date(this.endDate),
    });

    if (isCourse || isEvent) {
      duration = { days: duration.days + 1 }; // a 2 days course: 1d10h30m
    }

    return formatDuration(duration, options);
  }

  public get getBookingURL(): string {
    return this.offers
      ?.filter(this.isOfferExternal)
      .map((offer) => String(offer.meta.booking))
      .pop();
  }

  public isBookable(bookableUntilTheEnd = false, quantity = 1): boolean {
    if (this.status !== ('PUBLISHED' as TimeslotStatusEnum)) {
      return false;
    }

    if (quantity > this.remainingAttendeeCapacity) {
      return false;
    }

    if (bookableUntilTheEnd) {
      return true;
    }
    return new Date(this.startDate) >= new Date();
  }

  public isExternal(): boolean {
    return this.offers?.some(this.isOfferExternal) || false;
  }
}
